import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, XIcon, ArrowRightIcon, LayersIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { genres, subgenres, tropes, themes } from '../../data/bookTags';
const QUICK_MOODS = ['Dark Romance', 'Hockey Romance', 'Why Choose', 'Enemies to Lovers', 'Small Town Romance', 'Morally Grey Hero', 'Slow Burn', 'Paranormal Romance'];
// Build a dictionary of all known tags for parsing
const KNOWN_TAGS = [...genres.map(g => ({
  id: g.id,
  name: g.name.toLowerCase()
})), ...subgenres.map(s => ({
  id: s.id,
  name: s.name.toLowerCase()
})), ...tropes.map(t => ({
  id: t.id,
  name: t.name.toLowerCase()
})), ...themes.map(t => ({
  id: t.id,
  name: t.name.toLowerCase()
}))];
interface StoryStackHeroProps {
  onMoodSelect?: (tags: string[]) => void;
}
const StoryStackHero: React.FC<StoryStackHeroProps> = ({
  onMoodSelect
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const navigate = useNavigate();
  const [moodInput, setMoodInput] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // Notify parent when tags change
  useEffect(() => {
    if (onMoodSelect) {
      onMoodSelect(selectedTags);
    }
  }, [selectedTags, onMoodSelect]);
  const accentBg = isAfterTide ? 'bg-[#e2b04a]' : 'bg-[#7E8CE0]';
  const accentBgHover = isAfterTide ? 'hover:bg-[#d1a03a]' : 'hover:bg-[#6A78D1]';
  const accentBorder = isAfterTide ? 'border-[#e2b04a]' : 'border-[#7E8CE0]';
  const accentText = isAfterTide ? 'text-[#e2b04a]' : 'text-[#7E8CE0]';
  const accentFocusBorder = isAfterTide ? 'focus:border-[#e2b04a]' : 'focus:border-[#7E8CE0]';
  const accentHoverBorder = isAfterTide ? 'hover:border-[#e2b04a]' : 'hover:border-[#7E8CE0]';
  const accentHoverText = isAfterTide ? 'hover:text-[#e2b04a]' : 'hover:text-[#7E8CE0]';
  const accentBtnText = isAfterTide ? 'text-[#1a1a2e]' : 'text-white';
  const handleAddTag = (tag: string) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };
  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter(t => t !== tag));
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!moodInput.trim()) return;
    const inputLower = moodInput.toLowerCase();
    let foundTags: string[] = [];
    KNOWN_TAGS.forEach(tag => {
      if (inputLower.includes(tag.name)) {
        const quickMood = QUICK_MOODS.find(m => m.toLowerCase() === tag.name);
        const formattedTag = quickMood || tag.name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        foundTags.push(formattedTag);
      }
    });
    if (foundTags.length > 0) {
      const newTags = [...selectedTags];
      foundTags.forEach(tag => {
        if (!newTags.includes(tag)) newTags.push(tag);
      });
      setSelectedTags(newTags);
    } else {
      handleAddTag(moodInput.trim());
    }
    setMoodInput('');
  };
  const handleExplore = () => {
    navigate('/story-stack');
  };
  return <div className={`relative overflow-hidden rounded-2xl shadow-lg border ${dk.card}`} data-id="element-1851" data-mp="2xlzeyxp7h64">
      {/* Background decorative element */}
      <div className={`absolute top-0 right-0 -mt-10 -mr-10 ${accentText} opacity-[0.06] pointer-events-none`} data-id="element-1852" data-mp="3uayqwmy1slc8">
        <LayersIcon className="w-40 h-40 md:w-48 md:h-48" data-id="element-1853" data-mp="3pbca63j0gmyo" />
      </div>

      <div className="relative p-4 sm:p-6 md:p-8" data-id="element-1854" data-mp="3kfx1dpzv1h94">
        {/* Two-column layout on desktop, stacked on mobile */}
        <div className="flex flex-col md:flex-row md:items-start md:gap-8" data-id="element-1855" data-mp="3fehju7ww5svh">
          {/* Left: Headline + Search + Stack preview */}
          <div className="flex-1 min-w-0" data-id="element-1856" data-mp="3z7dpdvtol38">
            <h2 className={`text-lg sm:text-xl md:text-2xl font-bold mb-1.5 sm:mb-2 ${dk.textHeading}`} data-id="element-1857" data-mp="3zh432ks4u9g">
              {isAfterTide ? 'What dark story calls to you tonight?' : 'What story are you in the mood for?'}
            </h2>
            <p className={`text-xs sm:text-sm mb-3 sm:mb-4 ${dk.textSecondary}`} data-id="element-1858" data-mp="3vcad98rkh810">
              {isAfterTide ? 'Describe the intensity you crave, or pick moods to build your Story Stack.' : 'Search by mood, trope, or vibe — or pick from popular tags.'}
            </p>

            {/* Search input */}
            <form onSubmit={handleSearch} className="mb-3 sm:mb-4" data-id="element-1859" data-mp="3ceh96357is21">
              <div className="flex gap-2 sm:gap-0 sm:relative" data-id="element-1860" data-mp="3cgtqqf71z0ek">
                <div className="relative flex-1" data-id="element-1861" data-mp="16n3tc3fcmr3o">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${dk.textMuted}`} data-id="element-1862" data-mp="1andxdajkz7fw" />
                  <input type="text" value={moodInput} onChange={e => setMoodInput(e.target.value)} placeholder={isAfterTide ? 'Try: dark mafia enemies-to-lovers' : 'Try: dark enemies-to-lovers mafia romance'} className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-20 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base border-2 focus:outline-none ${accentFocusBorder} transition-colors ${dk.input} shadow-sm`} data-id="element-1863" data-mp="2v5y5qoiar0m8" />
                  <button type="submit" className={`hidden sm:block absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-1.5 ${accentBg} ${accentBgHover} ${accentBtnText} rounded-md font-semibold text-sm transition-colors`} data-id="element-1864" data-mp="3ntatv343vghn">
                    Add
                  </button>
                </div>
                <button type="submit" className={`sm:hidden flex-shrink-0 px-4 py-2.5 ${accentBg} ${accentBgHover} ${accentBtnText} rounded-lg font-semibold text-sm transition-colors`} data-id="element-1865" data-mp="3ok0xmjx4goum">
                  Add
                </button>
              </div>
            </form>

            {/* Selected stack preview — compact inline */}
            {selectedTags.length > 0 && <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3 sm:mb-0" data-id="element-1866" data-mp="bc67kqx0c0r8">
                <span className={`text-xs font-semibold uppercase tracking-wider mr-1 ${dk.textMuted}`} data-id="element-1867" data-mp="gahgmzfha78e">
                  Stack:
                </span>
                {selectedTags.map(tag => <span key={tag} className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 sm:py-1 ${rt.badgeRadius} ${accentBg} ${accentBtnText} text-xs font-medium`} data-id="element-1868" data-mp="q3eswntvmzyk">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="ml-1 opacity-70 hover:opacity-100" data-id="element-1869" data-mp="2jldv9b7yxlu5">
                      <XIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" data-id="element-1870" data-mp="2aj4isft53ie4" />
                    </button>
                  </span>)}
                <button onClick={handleExplore} className={`inline-flex items-center text-xs font-semibold ml-1 ${accentText} hover:underline`} data-id="element-1871" data-mp="25m4d4607chnj">
                  Explore →
                </button>
              </div>}
          </div>

          {/* Right: Quick mood pills */}
          <div className="md:w-[280px] lg:w-[320px] flex-shrink-0" data-id="element-1872" data-mp="2ojfdxv1hfj53">
            <p className={`hidden md:block text-xs font-semibold uppercase tracking-wider mb-2 ${dk.textMuted}`} data-id="element-1873" data-mp="2oj5lm0syszw8">
              Popular moods
            </p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-1874" data-mp="3h6i86az4l7pj">
              {QUICK_MOODS.map(mood => <button key={mood} onClick={() => handleAddTag(mood)} className={`px-2.5 py-1 sm:px-3 sm:py-1.5 ${rt.badgeRadius} text-xs font-medium border transition-all ${selectedTags.includes(mood) ? `${accentBg} ${accentBtnText} ${accentBorder}` : `${dk.surfaceMuted} ${dk.textSecondary} ${accentHoverBorder} ${accentHoverText}`}`} data-id="element-1875" data-mp="2pj732qwieq5p">
                  {mood}
                </button>)}
            </div>
            {/* Explore button on desktop when tags selected */}
            {selectedTags.length > 0 && <button onClick={handleExplore} className={`hidden md:flex items-center justify-center w-full mt-3 py-2 rounded-lg ${accentBg} ${accentBgHover} ${accentBtnText} font-semibold text-sm transition-colors shadow-sm`} data-id="element-1876" data-mp="2za1qvzpelxt9">
                Explore your Story Stack
                <ArrowRightIcon className="ml-1.5 h-4 w-4" data-id="element-1877" data-mp="1ruetwk8iw3rw" />
              </button>}
          </div>
        </div>
      </div>
    </div>;
};
export default StoryStackHero;